import PaneContainer from "@/layouts/components/PaneContainer"
import TwoColumn from "@/layouts/TwoColumn"
import ItemTreeTable from "./components/ItemTreeTable"
import ItemDetail from "./components/ItemDetail"
import CategoryDetail from "./components/categoryDetail"
import DateSelector from "./components/DateSelector"
import DateSummary from "./components/DateSummary"
import MachineSelector from "./components/MachineSelector"
import MachineSummary from "./components/MachineSummary"
import { Dialog } from "primereact/dialog"
import { Button } from "primereact/button"
import { Toast } from "primereact/toast"
import { Message } from "primereact/message"
import { ProgressBar } from "primereact/progressbar"
import Optimizer from "../CreatePlan/steps/Optimizer"
import FactoryChart from "./components/charts/FactoryChart"
import PlanActionBar from "./components/PlanActionBar"
import PlanComparisonDialog from "./components/PlanComparisonDialog"
import ExcelWritePreviewDialog from "./components/ExcelWritePreviewDialog"
import { usePlanPage } from "./hooks/usePlanPage"

const Plan = () => {
  const {
    toastRef,
    items,
    orderProducts,
    availableDates,
    excelPlanResponse,
    currentResponse,
    optimizationLogs,
    orders,
    productionTasks,
    inventoryAllocations,
    productInventory,
    compareOrders,
    compareProductionTasks,
    compareProductInventory,
    compareDates,
    itemsStatus,
    itemStats,
    productsById,
    inventories,
    shipments,
    productions,
    excelProductions,
    optimizerProductions,
    numericOptimizerProductions,
    numericProductions,
    selectedItems,
    //isCategory,
    selectedDate,
    selectedMachine,
    handleSelectionChange,
    setSelectedDate,
    setSelectedMachine,
    handleProductionChange,
    userProductionChanges,
    optimizerVisible,
    previewOptimizeRequest,
    optimizeQueryData,
    handleOptimize,
    handleOptimizeDialogClose,
    xmlModalVisible,
    setXmlModalVisible,
    setUserProductionChanges,
    actionBarConfig,
    excelReloadDialog,
    excelWriteDialog,
    comparisonDialog,
    excelPreviewDialog,
  } = usePlanPage()

  const isCategory = false
  return (
    <>
      <TwoColumn
        header={<PlanActionBar {...actionBarConfig} />}
        leftPane={(
          <PaneContainer>
            <ItemTreeTable
              products={orderProducts}
              itemStatus={itemsStatus}
              itemStats={itemStats}
              onSelectionChange={handleSelectionChange}
            />
            <DateSelector
              dates={availableDates}
              selectedDate={selectedDate}
              onDateChange={setSelectedDate}
              holidayDates={excelPlanResponse?.holidayDates}
              productionTasks={productionTasks}
              productsById={productsById}
            />
            <MachineSelector
              selectedMachine={selectedMachine}
              onMachineChange={setSelectedMachine}
              productionTasks={productionTasks}
              productsById={productsById}
              dates={availableDates}
              holidayDates={excelPlanResponse?.holidayDates}
            />
          </PaneContainer>
        )}
        rightPane={(
          <PaneContainer>
            {selectedDate ? (
              <DateSummary
                selectedDate={selectedDate}
                productsById={productsById}
                productionTasks={productionTasks}
                holidayDates={excelPlanResponse?.holidayDates}
              />
            ) : selectedMachine ? (
              <MachineSummary
                selectedMachine={selectedMachine}
                productsById={productsById}
                productionTasks={productionTasks}
                dates={availableDates}
                holidayDates={excelPlanResponse?.holidayDates}
              />
            ) : isCategory ? (
              <CategoryDetail
                items={selectedItems}
                itemStats={itemStats}
                inventories={inventories}
                shipments={shipments}
                productions={numericProductions}
                dates={currentResponse?.dates ?? []}
              />
            ) : (
              <ItemDetail
                items={selectedItems}
                itemStats={itemStats}
                productsById={productsById}
                inventories={inventories}
                shipments={shipments}
                productions={productions}
                dates={currentResponse?.dates ?? []}
                orders={orders}
                productionTasks={productionTasks}
                compareOrders={compareOrders}
                compareProductionTasks={compareProductionTasks}
                compareProductInventory={compareProductInventory}
                compareDates={compareDates}
                optimizationLogs={optimizationLogs}
                productInventory={productInventory}
                inventoryAllocations={inventoryAllocations}
                onProductionChange={handleProductionChange}
                excelProductions={excelProductions}
                optimizerProductions={optimizerProductions}
                userProductions={userProductionChanges}
                horizonStart={currentResponse?.horizonStart}
                horizonEnd={currentResponse?.horizonEnd}
              />
            )}
          </PaneContainer>
        )}
      />

      <Dialog header="最適化実行" visible={optimizerVisible} style={{ width: '50vw' }} onHide={handleOptimizeDialogClose}>
        {optimizeQueryData && previewOptimizeRequest && (
          <Optimizer
            originalResponse={optimizeQueryData}
            optimizeRequest={previewOptimizeRequest}
            onBack={handleOptimizeDialogClose}
            onProceed={handleOptimize}
          />
        )}
      </Dialog>

      <PlanComparisonDialog
        visible={comparisonDialog.visible}
        onHide={comparisonDialog.close}
        excelTasks={comparisonDialog.excelTasks}
        currentTasks={comparisonDialog.currentTasks}
        currentKpis={comparisonDialog.currentKpis}
        currentAlgorithm={comparisonDialog.currentAlgorithm}
        optimizeRequest={comparisonDialog.optimizeRequest}
        onSelectSku={comparisonDialog.selectSku}
      />

      <ExcelWritePreviewDialog
        visible={excelPreviewDialog.visible}
        isLoading={excelPreviewDialog.isLoading}
        lastLog={excelPreviewDialog.lastLog}
        error={excelPreviewDialog.error}
        preview={excelPreviewDialog.preview}
        onCancel={excelPreviewDialog.cancel}
        onConfirm={excelPreviewDialog.confirm}
      />

      <Dialog
        header="OneDrive再読み込み"
        visible={excelReloadDialog.visible}
        style={{ width: '32rem' }}
        closable={!excelReloadDialog.isExcelReloading}
        onHide={excelReloadDialog.close}
        footer={<Button label="閉じる" onClick={excelReloadDialog.close} disabled={excelReloadDialog.isExcelReloading} />}
      >
        <ul className="list-none m-0 p-0 flex flex-col gap-3">
          {excelReloadDialog.progress.map((step) => {
            const iconClass = {
              pending: 'pi pi-clock text-gray-400',
              active: 'pi pi-spin pi-spinner text-blue-500',
              completed: 'pi pi-check-circle text-green-500',
              error: 'pi pi-times-circle text-red-500',
            }[step.status]
            return (
              <li key={step.id} className="flex items-center gap-3">
                <div className="mt-2">
                  <i className={iconClass} style={{ fontSize: '1.25rem' }} />
                </div>
                <div>
                  <p className="m-0 font-medium">{step.label}</p>
                  {step.status === 'active' && <small className="text-gray-500">処理中...</small>}
                  {step.status === 'error' && <small className="text-red-500">エラーが発生しました</small>}
                </div>
              </li>
            )
          })}
        </ul>
        {excelReloadDialog.stage === 'success' && (
          <p className="text-green-600 mt-4 mb-0">最新データの取り込みが完了しました。</p>
        )}
        {excelReloadDialog.error && (
          <p className="text-red-500 mt-4 mb-0">{excelReloadDialog.error}</p>
        )}
      </Dialog>

      <Dialog
        header="Excelファイルに保存"
        visible={excelWriteDialog.visible}
        style={{ width: '32rem' }}
        closable={!excelWriteDialog.isWriting}
        onHide={excelWriteDialog.close}
        footer={<Button label="閉じる" onClick={excelWriteDialog.close} disabled={excelWriteDialog.isWriting} />}
      >
        <p className="text-sm text-600">
          Excelを開いたままだと書き込みに失敗します。ファイルを閉じてから実行してください。
        </p>
        {excelWriteDialog.isLocked && (
          <Message
            severity="warn"
            text="ファイルが他のアプリで開かれています。Excelを閉じてから再度お試しください。"
            className="mb-3"
          />
        )}
        <div className="mb-3">
          {excelWriteDialog.progress !== null ? (
            <ProgressBar value={Math.max(0, Math.min(100, parseFloat(excelWriteDialog.progress.toFixed(2))))} showValue={true} />
          ) : (
            <ProgressBar mode="indeterminate" />
          )}
          <small className="block mt-2 text-600">{excelWriteDialog.lastLog ?? 'ログを待機しています...'}</small>
        </div>
        {excelWriteDialog.isSuccess && (
          <>
            <Message severity="success" text="Excelファイルの書き込みが完了しました" className="mt-3" />
            <p className="text-sm text-600 mt-2">
              計算結果を確定するため、Excelを開いて反映内容を確認してください。
            </p>
          </>
        )}
        {excelWriteDialog.error && (
          <Message severity="error" text={excelWriteDialog.error} className="mt-3" />
        )}
      </Dialog>

      <Toast ref={toastRef} />
    </>
  )
}

export default Plan
